/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { getGatewayIdToken } from "./gatewaySession";

const exchangeRateApiBaseUrl = import.meta.env.VITE_EXCHANGE_RATE_API;

const exchangeRateApi = axios.create({
  baseURL: exchangeRateApiBaseUrl,
});

exchangeRateApi.interceptors.request.use(async (config) => {
  if ((config.method ?? "get").toLowerCase() === "get" && config.url === "/api/exchange-rate/last") {
    config.headers.delete("Authorization");
    return config;
  }
  config.headers.Authorization = `Bearer ${await getGatewayIdToken()}`;
  return config;
});

export interface ExchangeRate {
  id: string;
  from_currency: string;
  to_currency: string;
  rate: number;
  date: string;
  created_at: string;
  updated_at: string;
}

export interface CreateExchangeRateRequest {
  from_currency: string;
  to_currency: string;
  rate: number;
  date: string;
}

export interface UpdateExchangeRateRequest {
  id: string;
  from_currency?: string;
  to_currency?: string;
  rate?: number;
  date?: string;
}

export interface ListExchangeRatesResponse {
  exchange_rates: ExchangeRate[];
  next_page_token?: string;
}

export const createExchangeRate = async (
  exchangeRate: CreateExchangeRateRequest
): Promise<ExchangeRate> => {
  const response = await exchangeRateApi.post<ExchangeRate>(
    "/api/exchange-rate",
    exchangeRate
  );
  return response.data;
};

export const getExchangeRate = async (id: string): Promise<ExchangeRate> => {
  const response = await exchangeRateApi.get<ExchangeRate>(
    "/api/exchange-rate",
    {
      params: { id },
    }
  );
  return response.data;
};

export const listExchangeRates = async (
  limit = 10,
  startAfter?: string,
  fromCurrency?: string,
  toCurrency?: string,
  date?: string
): Promise<ListExchangeRatesResponse> => {
  const params: any = { limit, start_after: startAfter };
  if (fromCurrency) {
    params.from_currency = fromCurrency;
  }
  if (toCurrency) {
    params.to_currency = toCurrency;
  }
  if (date) {
    params.date = date;
  }
  const response = await exchangeRateApi.get<ListExchangeRatesResponse>(
    "/api/exchange-rate/list",
    { params }
  );
  return response.data;
};

export const updateExchangeRate = async (
  update: UpdateExchangeRateRequest
): Promise<void> => {
  await exchangeRateApi.put("/api/exchange-rate", update);
};

export const deleteExchangeRate = async (id: string): Promise<void> => {
  await exchangeRateApi.delete("/api/exchange-rate", {
    params: { id },
  });
};

// New API call for retrieving the most recent exchange rate.
export const getLastExchangeRate = async (): Promise<ExchangeRate> => {
  const response = await exchangeRateApi.get<ExchangeRate>(
    "/api/exchange-rate/last"
  );
  return response.data;
};
